import { startTransition, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ApiService from '../services/Api.service';
import { AppIdentifiers, setToken } from '../store/slices/token-state.slice';
import store, { RootState } from '../store/store';

/**
 * Claims are written into the JWT at login, so an account whose entitlements
 * changed since then carries a stale token until they sign in again. This tops
 * the token up once per launch when a claim we depend on is missing, which is
 * what lets a lifetime member see the early access option without being told
 * to log out and back in.
 */
export function useMembershipClaimsSync(): void {
  const token = useSelector(
    (state: RootState) => state.tokenState[AppIdentifiers.App],
  );
  const attempted = useRef(false);

  useEffect(() => {
    const accessToken = token?.accessToken;
    if (!accessToken || attempted.current) {
      return;
    }

    let claims: Record<string, unknown> | null = null;
    try {
      claims = JSON.parse(atob(accessToken.split('.')[1]));
    } catch {
      return;
    }

    // Tokens issued before lifetimeMember existed have no such key at all.
    // Once the key is present, whatever it says is current.
    if (claims && 'lifetimeMember' in claims) {
      return;
    }

    attempted.current = true;

    const sync = async () => {
      try {
        const chApi = ApiService.getApiClient();
        const data: any = await chApi.authControllerRefreshSubscriptions();
        if (!data?.access_token) {
          return;
        }
        startTransition(() => {
          store.dispatch(
            setToken(
              AppIdentifiers.App,
              data.access_token,
              token?.refreshToken ?? null,
              Number(data.bubble_expiration ?? token?.expiration ?? 0),
            ),
          );
        });
      } catch (error) {
        // Not being able to refresh is not worth interrupting the user over;
        // they keep the token they have and we try again next launch.
        console.error('Could not refresh membership claims:', error);
      }
    };

    void sync();
  }, [token]);
}
